import React from 'react';
import {
  I18nManager,
  ListRenderItem,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { Trans } from '@lingui/macro';
import { t } from '@lingui/macro';
import { useIsFocused, useNavigation } from '@react-navigation/native';

import Carousel, { Pagination } from '@src/components/Carousel';
import LocationTag from '@src/components/LocationTag';
import Row from '@src/components/Row';
import { Body1 } from '@src/components/Typography';
import UserTag from '@src/components/UserTag';
import { maxWidth } from '@src/configuration/constants';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n } from '@src/i18n';
import { Media, Post } from '@src/types';

import Likable from './Likable';
import ReportModal from './ReportModal';
import {
  CommentIconButton,
  Container,
  Content,
  ExtendedContent,
  Header,
  IconsRow,
  JourneyButton,
  JourneyText,
  LeftIcons,
  LikeIconButton,
  MediaContainer,
  MoreOptions,
  MoreOptionsIconButton,
  Photo,
  RightIcons,
  Thumbnail,
  Video,
} from './styled';
import useLike from './useLike';
import useRemove from './useRemove';

type Props = {
  extended?: boolean;
  data?: Post;
  play?: boolean;
  onRemove?: () => void;
};

enum MoreOption {
  Remove = 'remove',
  Report = 'report',
}

const Component: React.FC<Props> = (props) => {
  const { data, extended, play = false, onRemove } = props;
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const dimensions = useWindowDimensions();
  const [muted, setMuted] = React.useState(true);
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [reportVisible, setReportVisible] = React.useState(false);

  const handleTap = React.useCallback(() => setMuted(!muted), [muted]);
  const {
    tokens: { userID },
  } = useAuthenticationTokens();
  const snackBar = useSnackBar();

  const { handleRemove } = useRemove();
  const { handleLike, liked, likesCount } = useLike(
    data?.id,
    data?.liked,
    data?.likesCount
  );

  const handlePress = React.useCallback(
    () => navigation.navigate('Post', { id: data?.id }),
    [data, navigation]
  );
  const handleJourneyPress = React.useCallback(
    () =>
      extended
        ? navigation
            // @ts-ignore
            .replace('Journey', { id: data?.journey?.id })
        : navigation.navigate('Journey', { id: data?.journey?.id }),
    [data, extended, navigation]
  );

  const isUserPost = React.useMemo(() => userID === data?.user.id, [
    data,
    userID,
  ]);
  const moreOptionsList = React.useMemo(
    () => [
      {
        label: i18n._(t`Report`),
        value: MoreOption.Report,
      },
      {
        label: i18n._(t`Remove`),
        value: MoreOption.Remove,
      },
    ],
    []
  );

  const [currentVisibleIndex, setCurrentVisibleIndex] = React.useState(0);
  const height = React.useMemo(() => {
    return Math.min(
      maxWidth,
      dimensions.width,
      ...(data?.medias.map((media) =>
        media.height && media.width
          ? (media.height / media.width) * dimensions.height
          : dimensions.height
      ) ?? [])
    );
  }, [data, dimensions.height, dimensions.width]);
  const mediaList = React.useMemo(() => data?.medias ?? [], [data]);

  const handlePressMoreOption = React.useCallback(() => {
    setShowMoreOptions(true);
  }, []);

  const handleMoreOption = React.useCallback(
    async (option: MoreOption) => {
      switch (option) {
        case MoreOption.Remove: {
          if (!data?.id) {
            return;
          }
          try {
            await handleRemove(data.id);
            snackBar.show({ content: i18n._(t`Post Removed`) });
          } catch (error) {
            snackBar.show({ content: i18n._(t`Error in Removing Post`) });
          }
          onRemove?.();
          break;
        }
        case MoreOption.Report: {
          setReportVisible(true);
          break;
        }
        default:
          break;
      }
    },
    [data, handleRemove, onRemove, snackBar]
  );

  const onSubmitReportHandler = React.useCallback(() => {
    setReportVisible(false);
    snackBar.show({ content: i18n._(t`Reported Successfully`) });
  }, [snackBar]);

  const renderItem: ListRenderItem<Media> = React.useCallback(
    ({ item, index }) => {
      switch (item.type) {
        case 'PHOTO':
          return (
            <Photo
              width={dimensions.width}
              height={height}
              source={item.source}
            />
          );
        case 'VIDEO':
          const rtlIndex =
            Platform.OS === 'android'
              ? I18nManager.isRTL
                ? mediaList.length - currentVisibleIndex - 1
                : currentVisibleIndex
              : currentVisibleIndex;

          return (
            <Thumbnail
              width={dimensions.width}
              height={height}
              source={item.thumbnail ?? { uri: '' }}
            >
              {Platform.OS === 'web' ? (
                <Video
                  width={dimensions.width}
                  height={height}
                  playInBackground={false} // Audio continues to play when app entering background.
                  playWhenInactive={false}
                  repeat={true}
                  muted={muted}
                  paused={!(play && isFocused && index === rtlIndex)}
                  resizeMode="cover"
                  rate={1.0}
                  onError={console.log}
                  source={item.source}
                />
              ) : (
                play && (
                  <Video
                    width={dimensions.width}
                    height={height}
                    playInBackground={false} // Audio continues to play when app entering background.
                    playWhenInactive={false}
                    repeat={true}
                    muted={muted}
                    paused={!(isFocused && index === rtlIndex)}
                    resizeMode="cover"
                    rate={1.0}
                    onError={console.log}
                    source={item.source}
                  />
                )
              )}
            </Thumbnail>
          );
        default:
          return null;
      }
    },
    [
      dimensions.width,
      height,
      mediaList.length,
      currentVisibleIndex,
      muted,
      play,
      isFocused,
    ]
  );

  if (!data) {
    return null;
  }

  const { content, user, date, place, medias, journey, commentsCount } = data;
  return (
    <Container>
      <Header>
        <UserTag date={date} user={user} />
        <LocationTag barStyle="light-content" place={place} />
      </Header>
      <Likable onTap={handleTap} onLike={handleLike} liked={liked}>
        <MediaContainer width={dimensions.width} height={height}>
          <Carousel
            inactiveSlideScale={1}
            data={medias}
            enableSnap
            lockScrollWhileSnapping
            renderItem={renderItem}
            horizontal
            itemWidth={dimensions.width}
            sliderWidth={dimensions.width}
            onSnapToItem={setCurrentVisibleIndex}
            removeClippedSubviews
          />
        </MediaContainer>
      </Likable>
      <Pagination
        dotsLength={medias.length}
        activeDotIndex={currentVisibleIndex}
      />

      <IconsRow>
        <RightIcons>
          <LikeIconButton icon="heart-o" liked={liked} onPress={handleLike} />
          <Body1>
            <Trans>{likesCount} like</Trans>
          </Body1>
          <CommentIconButton onPress={extended ? undefined : handlePress} />
          <Body1>
            <Trans>{commentsCount} comment</Trans>
          </Body1>
        </RightIcons>
        <LeftIcons>
          <MoreOptions
            anchor={<MoreOptionsIconButton onPress={handlePressMoreOption} />}
            setValue={handleMoreOption}
            list={moreOptionsList.filter(({ value }) => {
              if (!isUserPost) {
                return value !== MoreOption.Remove;
              } else {
                return true;
              }
            })}
            visible={showMoreOptions}
            onShow={() => setShowMoreOptions(true)}
            onDismiss={() => setShowMoreOptions(false)}
          />
        </LeftIcons>
      </IconsRow>

      {journey && (
        <Row>
          <JourneyButton onPress={handleJourneyPress} />
          <TouchableOpacity onPress={handleJourneyPress}>
            <JourneyText>{journey.name}</JourneyText>
          </TouchableOpacity>
        </Row>
      )}

      {extended ? (
        <ExtendedContent>{content}</ExtendedContent>
      ) : (
        <Content>
          <Body1>{content.trim()}</Body1>
        </Content>
      )}

      <ReportModal
        post={data}
        visible={reportVisible}
        onDismiss={() => setReportVisible(false)}
        onSubmit={onSubmitReportHandler}
      />
    </Container>
  );
};

Component.displayName = 'PostCard';

export default React.memo(Component);
